import { Component, OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { Subscription } from 'rxjs';

import { languageService, languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';

import { AskWaiterView } from '@unpispas/upp-data';
import { dataService } from '@unpispas/upp-data';
import { QrCodeView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';

import { ViewRenderer } from '@unpispas/upp-wdgt';

type CounterPayAmount = { Int: string; Dec: string; Str: string; Neg: boolean };

/** Counter overlay when the place has no table service. */
@Component({
    selector: 'upp-guest-action',
    templateUrl: './upp-action.html',
    styleUrls: ['./upp-action.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppGuestActionComponent extends languageSupport implements OnInit, OnDestroy {
    @Input() qrcode: QrCodeView | null = null;
    @Output() Close = new EventEmitter<void>();

    private _qrcode_subscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
        public view: viewService,
    ) {
        super(lang, change.cdref);
    }

    get Requests(): Array<AskWaiterView> {
        return this.qrcode?.requests ?? [];
    }

    get TicketRequests(): Array<AskWaiterView> {
        return this.Requests.filter((request) => !!request.ticket);
    }

    get PayMethod(): 'PAYCASH' | 'PAYCARD' | null {
        const _reason = this.qrcode?.WaiterReason;
        if ((_reason === 'PAYCASH') || (_reason === 'PAYCARD')) {
            return _reason;
        }
        return null;
    }

    get PayAmount(): CounterPayAmount | null {
        const _amount = this.qrcode?.WaiterAmount ?? 0;
        return (_amount !== 0) ? UppGuestActionComponent._FormatAmount(_amount) : null;
    }

    private _showticket: TicketView | null = null;
    get ShowTicket(): TicketView | null {
        return this._showticket;
    }

    OnShowTicket(ticket: TicketView | null): void {
        this._showticket = ticket;
        this.change.markForCheck();
    }

    ngOnInit(): void {
        if (this.qrcode) {
            this._qrcode_subscription = this.qrcode.OnRefresh.subscribe(
            () => {
                this.change.markForCheck();
            });
        }
    }

    ngOnDestroy(): void {
        if (this._qrcode_subscription) {
            this._qrcode_subscription.unsubscribe();
            this._qrcode_subscription = null;
        }
        super.OnDestroy();
    }

    async OnCancel(): Promise<void> {
        const _transaction = this.data.transaction;

        for (const request of this.Requests) {
            const ticket = request.ticket;
            if (ticket && ((request.reason === 'PAYCASH') || (request.reason === 'PAYCARD'))) {
                ticket.CancelPayment();
                if (ticket.IsPrepay) {
                    ticket.status = 'PR';
                }
                await _transaction.push(ticket.object);
            }

            request.status = 'DE';
            await _transaction.push(request.object);
        }

        await _transaction.flush();
        this.Close.emit();
    }

    OnClose(): void {
        this.Close.emit();
    }

    RequestTrack(_index: number, request: AskWaiterView): string | null {
        return request?._uuid || null;
    }

    private static _FormatAmount(amount: number): CounterPayAmount {
        const _parts = Math.abs(amount).toString().split('.');
        const _int = _parts[0];
        const _dec = (_parts.length > 1) ? (_parts[1] + '0').slice(0, 2) : '00';
        return { Int: _int, Dec: _dec, Str: `${_int},${_dec}`, Neg: (amount < 0) };
    }
}
